import { Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { FirebaseMessagingService } from './firebase-messaging.service';
import { NotificationService, ISubscribeTokenPayload } from './notification.service';
import { NotificationStateService } from './notification-state.service';
import { AuthService } from './auth.service';
import { NotificationToastService } from './notification-toast.service';

@Injectable({
  providedIn: 'root'
})
export class NotificationInitService {
  private jwtHelper: JwtHelperService = new JwtHelperService();
  private isInitialized = false;

  constructor(
    private firebaseMessagingService: FirebaseMessagingService,
    private notificationService: NotificationService,
    private notificationStateService: NotificationStateService,
    private authService: AuthService,
    private messageService: MessageService,
    private translateService: TranslateService,
    private notificationToastService: NotificationToastService,
    private router: Router
  ) {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        console.log('[NotificationInit] SW message received:', event.data);
        if (event.data?.type === 'PLAY_NOTIFICATION_SOUND') {
          this.notificationToastService.playNotificationSound();
        } else if (event.data?.type === 'NAVIGATE' && event.data?.url) {
          this.router.navigateByUrl(event.data.url);
        }
      });
    }
  }

  /**
   * Initialize notification system
   * - Check authentication
   * - Request permission
   * - Get FCM token
   * - Subscribe token to backend
   * - Initialize state service
   * - Start listening to foreground messages
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) {
      console.log('[NotificationInit] Already initialized');
      return;
    }

    console.log('[NotificationInit] Starting initialization...');

    try {
      // Check if user is authenticated
      if (!this.authService.isAuthenticated()) {
        console.log('[NotificationInit] User not authenticated, skipping initialization');
        return;
      }

      // Check if browser supports notifications
      if (!this.firebaseMessagingService.isSupported()) {
        console.warn('[NotificationInit] Browser does not support notifications');
        return;
      }

      // Request notification permission
      const permissionGranted = await this.firebaseMessagingService.requestPermission();

      if (!permissionGranted) {
        console.warn('[NotificationInit] Notification permission denied');
        this.showPermissionDeniedMessage();
        return;
      }

      // Get FCM token
      const fcmToken = await this.firebaseMessagingService.getToken();

      if (!fcmToken) {
        console.error('[NotificationInit] Failed to get FCM token');
        this.showTokenErrorMessage();
        return;
      }

      // Get push subscription keys
      const subscriptionKeys = await this.firebaseMessagingService.getPushSubscriptionKeys();

      if (!subscriptionKeys) {
        console.error('[NotificationInit] Failed to get push subscription keys');
        this.showTokenErrorMessage();
        return;
      }

      // Extract userId from JWT token
      const userId = this.extractUserIdFromToken();

      if (!userId) {
        console.error('[NotificationInit] Failed to extract userId from token');
        return;
      }

      // Subscribe token to backend
      const payload: ISubscribeTokenPayload = {
        userId: userId,
        endpoint: subscriptionKeys.endpoint,
        expirationTime: subscriptionKeys.expirationTime ? subscriptionKeys.expirationTime.toString() : null,
        keys: {
          p256dh: subscriptionKeys.p256dh,
          auth: subscriptionKeys.auth
        },
        firebaseToken: fcmToken,
        notificationService: 'firebase'
      };

      await this.subscribeTokenToBackend(payload);

      // Initialize state service
      this.notificationStateService.initialize();

      // Start listening to foreground messages
      this.firebaseMessagingService.listenToForegroundMessages();

      this.isInitialized = true;
      console.log('[NotificationInit] Initialization complete');
    } catch (error) {
      console.error('[NotificationInit] Initialization failed:', error);
    }
  }

  /**
   * Extract userId from JWT token stored in localStorage
   * @returns string | null
   */
  private extractUserIdFromToken(): string | null {
    try {
      const storedToken = localStorage.getItem('token');

      if (!storedToken) {
        return null;
      }

      const token = storedToken.startsWith('Bearer ')
        ? storedToken.slice(7)
        : storedToken;

      const decodedToken = this.jwtHelper.decodeToken(token);

      // Try different possible field names for user ID
      return decodedToken?.userId || decodedToken?.id || decodedToken?.sub || null;
    } catch (error) {
      console.error('[NotificationInit] Error extracting userId from token:', error);
      return null;
    }
  }

  /**
   * Subscribe FCM token to backend
   * @param payload - Token subscription payload
   */
  private async subscribeTokenToBackend(payload: ISubscribeTokenPayload): Promise<void> {
    return new Promise((resolve, reject) => {
      this.notificationService.subscribeToken(payload).subscribe({
        next: (response) => {
          console.log('[NotificationInit] Token subscribed successfully:', response);
          resolve();
        },
        error: (error) => {
          console.error('[NotificationInit] Error subscribing token:', error);
          reject(error);
        }
      });
    });
  }

  /**
   * Show permission denied toast message
   */
  private showPermissionDeniedMessage(): void {
    this.messageService.add({
      severity: 'warn',
      summary: this.translateService.instant('notifications.permissionDenied'),
      detail: this.translateService.instant('notifications.permissionDeniedDetail'),
      life: 5000
    });
  }

  /**
   * Show token error toast message
   */
  private showTokenErrorMessage(): void {
    this.messageService.add({
      severity: 'error',
      summary: this.translateService.instant('notifications.tokenError'),
      detail: this.translateService.instant('notifications.tokenError'),
      life: 5000
    });
  }

  /**
   * Reset initialization state (useful for logout/login scenarios)
   */
  reset(): void {
    this.isInitialized = false;
  }
}
